import React from 'react';
import Link from 'next/link';
import { GetStaticProps } from 'next';
import Head from '../../../components/head';
import { DateTime, Duration } from 'luxon';
import camelcaseKeys from 'camelcase-keys';
import { Nav, Row, Col, Card, Badge } from 'react-bootstrap';
import Prismic from 'prismic-javascript';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faClock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import Layout from '../../../components/Layout';
import SVG from 'react-inlinesvg';
import OpenGraphTitle from '../../../components/OpenGraphTitle';
import OpenGraphDescription from '../../../components/OpenGraphDescription';
import Section from '../../../components/Section';
import PageTitle from '../../../components/PageTitle';
import Paginator from '../../../components/Paginator';
import { PrismicClient } from '../../../SDKs/Prismic/prismic.config';
import { BlogItemDataCamelCase } from '../../../SDKs/Prismic/prismic.types';
// import BlogImageCard from '../../components/BlogImageCard';
import { FranchiseeProvider } from '../../../context/Franchisee';
import Image from 'next/image';

const iconPageNotFound = '/images/icon-404.svg';

export const getStaticProps: GetStaticProps = async (context) => {
  // const { req } = context;
  const { pageNumber } = context.params;

  if (/\D+/g.test(pageNumber.toString())) {
    return { notFound: true };
  }

  // convert pageNumber to int.
  const numberOfPages = parseInt(pageNumber.toString(), 10) || 1;

  // get the page.
  const queryResult = await PrismicClient().query(
    Prismic.Predicates.at('my.blog-post.franchiseeslug', 'general'),
    {
      pageSize: 6,
      page: numberOfPages,
      orderings: '[my.blog-post.dateposted desc]',
    }
  );

  const {
    page,
    prev_page: prevPage,
    next_page: nextPage,
    results_per_page: resultsPerPage,
    results_size: resultsSize,
    total_pages: totalPages,
    results,
  } = await queryResult;

  const blogItems = results
    .filter(({ uid }) => uid !== null)
    .map((item) => camelcaseKeys({ ...item, data: camelcaseKeys(item.data) }));

  const meta = {
    page,
    prevPage,
    nextPage,
    resultsPerPage,
    resultsSize,
    totalPages,
  };

  return {
    props: {
      meta,
      blogItems,
    },
    // revalidate: Duration.fromObject({ minutes: 15 }).as('seconds'),
    revalidate: Duration.fromObject({ hours: 8 }).as('seconds'),
  };
};

export const getStaticPaths = async () => {
  const paths = [{ params: { pageNumber: '1' } }];

  return {
    paths,
    fallback: 'blocking',
  };
};

interface BlogPageProps {
  /* add blog page props here */
  blogItems: Array<{
    id: string;
    firstPublicationDate: string;
    lastPublicationDate: string;
    type: string;
    slugs: Array<string>;
    tags: Array<string>;
    data: BlogItemDataCamelCase;
    uid: string;
  }>;
  meta: {
    page: number;
    prevPage?: number;
    nextPage?: number;
    resultsPerPage?: number;
    resultsSize: number;
    totalPages: number;
  };
}

const Blog: React.FC<BlogPageProps> = ({ blogItems, meta }: BlogPageProps) => 
(
  <FranchiseeProvider>
    <Layout alternateFooter>
      <Head slug='blog' title='Blog - Two Maids - Professional Cleaning Services' description='Tips, updates, stories from the field, and information on our professional cleaning services from our blogs.'/>
      <OpenGraphTitle title='Blog - Two Maids - Professional Cleaning Services' />

      <OpenGraphDescription>
        Tips, updates, stories from the field, and information on our professional cleaning services from our blogs.
      </OpenGraphDescription>
      <div className='top-background-dots'></div>
      <Section lg={12} md={12}>
        <PageTitle className=''>
          Two Maids Cleaning Blog
        </PageTitle>

        <Row style={{justifyContent: 'space-between', rowGap: '4rem'}}>
          {meta.resultsSize ? (
            blogItems.map(({ data, id, firstPublicationDate, uid, tags }) => {
              const publishedDate = DateTime.fromISO(data.dateposted);
              const fallbackDate = DateTime.fromISO(firstPublicationDate);
              return (
                <Link key={uid} href={`/blog/${uid}`}>
                  <Col
                    className='mb-4'
                    xs={12}
                    md={4}
                    style={{ textAlign: 'center' }}
                  >
                    <Card
                      key={id}
                      style={{ width: '100%', height: '100%' }}
                      className='blog-card-new'
                    >
                      <Row>
                        {data.banner.url && (
                          <Col xs={12} md={12}>
                            <Image
                              className='blog-card-image'
                              src={data.banner.url}
                              alt={data.banner.alt}
                              width='300px'
                              height='300px'
                              placeholder='blur'
                              blurDataURL={data.banner.url}
                            />
                          </Col>
                        )}
                        <Col xs={12} md={12}>
                          <Card.Title
                            className='px-4 pt-4 ellipsis blog-card-title'
                            style={{
                              fontSize: '18px',
                              lineHeight: '26px',
                              overflow: 'hidden',
                              fontWeight: 'bold',
                              color: 'var(--dark-pink)',
                              fontFamily: 'Gotham Medium',
                            }}
                          >
                            <span>{data.blogTitle[0]?.text}</span>
                          </Card.Title>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={12} className='px-4'>
                          <Card.Text style={{fontSize: 14, color: 'var(--dark-pink)'}}>
                            {
                              publishedDate ?
                                publishedDate.toLocaleString({ locale: "en-US" })
                                : fallbackDate.toLocaleString({ locale: "en-US" })
                            }
                          </Card.Text>
                        </Col>
                      </Row>
                      <Row>
                        {data.authorName !== null && (
                        <Col xs={12} md={12} className='px-4'>
                            <>
                              {/* fontWeight needs to be medium and not throw an error */}
                              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--dark-pink)' }}>
                                By: {data.authorName}
                              </span>
                            </>
                        </Col>
                        )}
                      </Row>
                      <Row className='px-2 pb-4 badge-list'>
                          {tags.length > 0 && (
                            <>
                              <br />
                              {tags.slice(0, 6).map(
                                (tag) =>
                                  tag.length < 14 && (
                                    <Badge
                                      key={tag}
                                      className='px-2'
                                      style={{
                                        fontSize: '14px',
                                        color: 'var(--dark-pink)',
                                        fontFamily: 'Gotham Medium',
                                        textTransform: 'uppercase'
                                      }}
                                    >
                                      {tag}
                                    </Badge>
                                  )
                              )}
                            </>
                          )}
                      </Row>
                    </Card>
                  </Col>
                </Link>
              );
            })
          ) : (
            <Col xs={10} lg={12} xl={10}>
              <SVG
                src={iconPageNotFound}
                title='A piece of paper'
                className='w-100 mb-4'
              />

              <h1 className='h2 text-center mb-5'>
                We&apos;re Sorry. Two Maids  hasn&apos;t posted any
                content yet.
              </h1>

              <p className='text-center'>
                Maybe one of these links will help you find what you&apos;re
                looking for.
              </p>
              <Nav className='d-block d-lg-flex justify-content-center my-5'>
                <Nav.Item>
                  <Nav.Link>
                    <Link href='/locations/'>
                      <a className='btn btn-outline-primary btn-block'>
                        Find a Store <FontAwesomeIcon icon={faSearch} />
                      </a>
                    </Link>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link>
                    <Link href='/services/'>
                      <a className='btn btn-outline-primary btn-block'>
                        Cleaning Packages
                      </a>
                    </Link>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link>
                    <Link href='/book/'>
                      <a className='btn btn-outline-primary btn-block'>
                        Get a Quote
                      </a>
                    </Link>
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
          )}
        </Row>

        {meta.totalPages > 1 && (
          <Row className='justify-content-center'>
            <Col xs={12} md={8} className='d-flex justify-content-end'>
              <Paginator meta={meta} hrefBase='/blog/page/' />
            </Col>
          </Row>
        )}
      </Section>
    </Layout>
  </FranchiseeProvider>
);

export default Blog;
